import React, { useState } from 'react';
import { executeMutation } from '../../graphql';
import { subscribeToSwiftMailingList } from '../../graphql/mutations';
import styles from './index.module.css';

const VALIDATION = {
  firstName: {
    min: 2, max: 50,
    pattern: /^[A-Za-z\s-]+$/,
    requiredMessage: 'Please enter your first name',
    lengthMessage: 'First name must be between 2 and 50 characters',
    patternMessage: 'First name can only contain letters, spaces and hyphens',
  },
  lastName: {
    min: 2, max: 50,
    pattern: /^[A-Za-z\s-]+$/,
    requiredMessage: 'Please enter your last name',
    lengthMessage: 'Last name must be between 2 and 50 characters',
    patternMessage: 'Last name can only contain letters, spaces and hyphens',
  },
  email: {
    min: null, max: 254,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    requiredMessage: 'Please enter your email address',
    lengthMessage: 'Email must not exceed 254 characters',
    patternMessage: 'Please enter a valid email address',
  },
  phone: {
    min: 10, max: 10,
    pattern: /^0[2-478]\d{8}$/,
    requiredMessage: 'Please enter your phone number',
    lengthMessage: 'Phone number must be 10 digits',
    patternMessage: 'Please enter a valid Australian phone number (10 digits starting with 0)',
  },
};

function validateValue(value, rule) {
  if (!value) return rule.requiredMessage ? { error: rule.requiredMessage } : null;
  if (rule.min !== null && value.length < rule.min) return { error: rule.lengthMessage };
  if (rule.max !== null && value.length > rule.max) return { error: rule.lengthMessage };
  if (rule.pattern && !rule.pattern.test(value)) return { error: rule.patternMessage };
  return null;
}

function validateExtraField(value, field) {
  if (field.fieldType === 'checkbox') {
    if (field.required && value !== 'Yes') return `Please tick the checkbox to proceed`;
    return null;
  }
  if (field.fieldType === 'radio') {
    if (field.required && !value) return `Please select an option for ${field.label.toLowerCase()}`;
    return null;
  }
  if (field.required && !value) return `Please enter your ${field.label.toLowerCase()}`;
  if (!value) return null;
  if (field.maxLength && value.length > field.maxLength) return `${field.label} must not exceed ${field.maxLength} characters`;
  if (field.validationPattern) {
    try {
      if (!new RegExp(field.validationPattern).test(value)) {
        return field.validationMessage || `Please enter a valid ${field.label.toLowerCase()}`;
      }
    } catch { /* invalid regex — skip */ }
  }
  return null;
}

function parseOptions(optionsStr) {
  if (!optionsStr) return [];
  return optionsStr.split(',').map(part => {
    const colon = part.indexOf(':');
    if (colon === -1) return { value: part.trim(), label: part.trim() };
    return { value: part.slice(0, colon).trim(), label: part.slice(colon + 1).trim() };
  });
}

function initialExtraValues(extraFields) {
  return Object.fromEntries(
    (extraFields || []).map(f => [
      f.swiftKey,
      f.fieldType === 'checkbox' ? 'No' : (f.defaultValue ?? ''),
    ])
  );
}

const SwiftSubscribeForm = ({
  heading,
  subtext,
  groupId,
  showPhone,
  privacyUrl,
  collectionNoticeUrl,
  successMessage,
  extraFields = [],
}) => {
  const [values, setValues] = useState(() => ({
    firstName: '', lastName: '', email: '', phone: '',
    ...initialExtraValues(extraFields),
  }));
  const [fieldErrors, setFieldErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [responseMessage, setResponseMessage] = useState('');

  const set = (key, val) => {
    setValues(v => ({ ...v, [key]: val }));
    setFieldErrors(e => ({ ...e, [key]: null }));
  };

  const runValidation = () => {
    const errors = {};

    const firstErr = validateValue(values.firstName.trim(), VALIDATION.firstName);
    if (firstErr) errors.firstName = firstErr.error;

    const lastErr = validateValue(values.lastName.trim(), VALIDATION.lastName);
    if (lastErr) errors.lastName = lastErr.error;

    const emailErr = validateValue(values.email.trim(), VALIDATION.email);
    if (emailErr) errors.email = emailErr.error;

    if (showPhone) {
      const phoneErr = validateValue(values.phone.trim(), VALIDATION.phone);
      if (phoneErr) errors.phone = phoneErr.error;
    }

    for (const field of extraFields) {
      if (field.fieldType === 'hidden') continue;
      const err = validateExtraField(values[field.swiftKey] ?? '', field);
      if (err) errors[field.swiftKey] = err;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!runValidation()) {
      setStatus('error');
      setResponseMessage('Please correct the errors above.');
      return;
    }

    setStatus('submitting');
    setResponseMessage('');

    const extra = extraFields
      .map(f => ({
        key: f.swiftKey,
        value: (values[f.swiftKey] ?? f.defaultValue ?? '').trim(),
        swiftPrefix: f.swiftPrefix || 'st',
      }))
      .filter(({ value }) => value !== '');

    try {
      const data = await executeMutation(subscribeToSwiftMailingList, {
        input: {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          email: values.email.trim(),
          ...(showPhone && values.phone ? { phone: values.phone.trim() } : {}),
          groupId,
          ...(extra.length ? { extraFields: extra } : {}),
        },
      });

      const result = data?.subscribeToSwiftMailingList;
      if (result?.success) {
        setStatus('success');
        setResponseMessage(successMessage || result.message || 'Thank you for subscribing!');
        e.target.reset();
        setValues({
          firstName: '', lastName: '', email: '', phone: '',
          ...initialExtraValues(extraFields),
        });
      } else {
        setStatus('error');
        setResponseMessage(result?.message || 'Subscription failed. Please try again.');
      }
    } catch {
      setStatus('error');
      setResponseMessage('An unexpected error occurred. Please try again later.');
    }
  };

  return (
    <div className={styles.formSection}>
      {heading && <p className={styles.heading}>{heading}</p>}
      {subtext && <p className={styles.subtext}>{subtext}</p>}

      <form onSubmit={handleSubmit} autoComplete="off" noValidate>
        <Field label="First name" id="firstName" required error={fieldErrors.firstName}>
          <input
            id="firstName"
            type="text"
            value={values.firstName}
            onChange={e => set('firstName', e.target.value)}
            required
          />
        </Field>

        <Field label="Last name" id="lastName" required error={fieldErrors.lastName}>
          <input
            id="lastName"
            type="text"
            value={values.lastName}
            onChange={e => set('lastName', e.target.value)}
            required
          />
        </Field>

        <Field label="Email" id="email" required error={fieldErrors.email}>
          <input
            id="email"
            type="email"
            value={values.email}
            onChange={e => set('email', e.target.value)}
            required
          />
        </Field>

        {showPhone && (
          <Field label="Phone" id="phone" required error={fieldErrors.phone}>
            <input
              id="phone"
              type="tel"
              value={values.phone}
              onChange={e => set('phone', e.target.value.replace(/[^0-9]/g, ''))}
              required
            />
          </Field>
        )}

        {(extraFields || []).map(field => {
          if (field.fieldType === 'hidden') return null;

          if (field.fieldType === 'radio') {
            const opts = parseOptions(field.options);
            return (
              <Field
                key={field.swiftKey}
                label={field.label}
                id={field.swiftKey}
                required={field.required}
                error={fieldErrors[field.swiftKey]}
              >
                <div className={styles.radioGroup}>
                  {opts.map(opt => (
                    <label key={opt.value} className={styles.radioOption}>
                      <input
                        type="radio"
                        name={field.swiftKey}
                        value={opt.value}
                        checked={values[field.swiftKey] === opt.value}
                        onChange={() => set(field.swiftKey, opt.value)}
                        required={field.required}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </Field>
            );
          }

          if (field.fieldType === 'checkbox') {
            return (
              <div
                key={field.swiftKey}
                className={`${styles.formGroup} ${fieldErrors[field.swiftKey] ? styles.hasError : ''}`}
              >
                <label className={styles.checkboxOption}>
                  <input
                    type="checkbox"
                    name={field.swiftKey}
                    checked={values[field.swiftKey] === 'Yes'}
                    onChange={e => set(field.swiftKey, e.target.checked ? 'Yes' : 'No')}
                  />
                  {field.label}
                </label>
                {fieldErrors[field.swiftKey] && (
                  <div className={styles.fieldError}>{fieldErrors[field.swiftKey]}</div>
                )}
              </div>
            );
          }

          if (field.fieldType === 'textarea') {
            return (
              <Field
                key={field.swiftKey}
                label={field.label}
                id={field.swiftKey}
                required={field.required}
                error={fieldErrors[field.swiftKey]}
              >
                <textarea
                  id={field.swiftKey}
                  name={field.swiftKey}
                  value={values[field.swiftKey] || ''}
                  placeholder={field.placeholder || ''}
                  maxLength={field.maxLength || undefined}
                  required={field.required}
                  onChange={e => set(field.swiftKey, e.target.value)}
                />
              </Field>
            );
          }

          return (
            <Field
              key={field.swiftKey}
              label={field.label}
              id={field.swiftKey}
              required={field.required}
              error={fieldErrors[field.swiftKey]}
            >
              <input
                id={field.swiftKey}
                type={field.fieldType || 'text'}
                value={values[field.swiftKey] || ''}
                placeholder={field.placeholder || ''}
                maxLength={field.maxLength || undefined}
                required={field.required}
                onChange={e => set(field.swiftKey, e.target.value)}
              />
            </Field>
          );
        })}

        {(privacyUrl || collectionNoticeUrl) && (
          <div className={styles.privacyNote}>
            <p>
              By subscribing, I agree to receiving news and promotions about VicRoads Custom
              Plates and related partner products. Please read our{' '}
              {collectionNoticeUrl && (
                <a href={collectionNoticeUrl} target="_blank" rel="noopener noreferrer">
                  Collection Notice
                </a>
              )}
              {collectionNoticeUrl && privacyUrl && ' and '}
              {privacyUrl && (
                <a href={privacyUrl} target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
              )}.
            </p>
          </div>
        )}

        <button
          type="submit"
          className={styles.btnSubmit}
          disabled={status === 'submitting'}
        >
          {status === 'submitting' ? 'Submitting...' : 'Submit'}
        </button>

        {responseMessage && (
          <div className={`${styles.responseMsg} ${styles[status]}`}>
            {responseMessage}
          </div>
        )}
      </form>
    </div>
  );
};

const Field = ({ label, id, required, error, children }) => (
  <div className={`${styles.formGroup} ${required ? styles.required : ''} ${error ? styles.hasError : ''}`}>
    <label htmlFor={id}>{label}</label>
    {children}
    {error && <div className={styles.fieldError}>{error}</div>}
  </div>
);

export default SwiftSubscribeForm;
